import type { Goal } from "api/types/aba";
import type { GoalResource } from "../shared/ResourcesCard";
import { HYPOTHESIZED_FUNCTION_LABELS } from "../shared/behaviorFunctions";
import type { GoalType } from "../Goals/GoalOverviewCard";
import type { GoalTarget } from "../Targets/GoalTargetsList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CriteriaNote {
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/**
 * Maps Goal.resources (API shape) to GoalResource[] for ResourcesCard.
 * Returns an empty array when resources is null/undefined.
 */
export function mapGoalResources(resources: Goal["resources"]): GoalResource[] {
  if (!Array.isArray(resources)) return [];
  return resources.map((r, i) => ({ id: String(i), label: r.title, url: r.url }));
}

/**
 * Maps the API snake-case goal_type to the camelCase variant expected by
 * GoalOverviewCard and related components.
 */
export function mapGoalType(goalType: Goal["goal_type"]): GoalType {
  return goalType === "skill-acquisition" ? "skillAcquisition" : "behaviorReduction";
}

/**
 * Builds the mastery criteria note rows for GoalNotesCard.
 * Omits fields that are null or empty — returns [] when nothing to show.
 */
export function mapMasteryCriteriaNotes(
  goal: Pick<Goal, "mastery_definition" | "generalization_definition" | "regression_criteria">
): CriteriaNote[] {
  return [
    goal.mastery_definition
      ? { label: "Mastery definition", value: goal.mastery_definition }
      : null,
    goal.generalization_definition
      ? { label: "Generalization definition", value: goal.generalization_definition }
      : null,
    goal.regression_criteria
      ? { label: "Regression criteria", value: goal.regression_criteria }
      : null,
  ].filter((n): n is CriteriaNote => n !== null);
}

/**
 * Builds the behavior-reduction note rows for GoalNotesCard from the
 * BR-specific GetGoal fields (builder Section 1.5). Returns [] for
 * skill-acquisition goals or when every field is empty — callers should
 * skip rendering the card in that case.
 */
export function mapBehaviorReductionNotes(
  goal: Pick<
    Goal,
    "goal_type" | "operational_definition" | "hypothesized_functions" | "common_antecedents"
  >
): CriteriaNote[] {
  if (goal.goal_type !== "behavior-reduction") return [];

  const functions = (goal.hypothesized_functions ?? [])
    .map((f) => HYPOTHESIZED_FUNCTION_LABELS[f] ?? f)
    .join(", ");

  return [
    goal.operational_definition
      ? { label: "Operational definition", value: goal.operational_definition }
      : null,
    functions ? { label: "Hypothesized function(s)", value: functions } : null,
    goal.common_antecedents
      ? { label: "Common antecedents", value: goal.common_antecedents }
      : null,
  ].filter((n): n is CriteriaNote => n !== null);
}

/**
 * Builds the instruction note rows for GoalNotesCard.
 * Per product spec the Instructions card self-hides when all fields are empty,
 * so callers should skip rendering when the returned array is empty.
 */
export function mapInstructionNotes(
  goal: Pick<Goal, "general_session_instructions" | "reinforcement_context" | "strategy_notes">
): CriteriaNote[] {
  return [
    goal.general_session_instructions
      ? { label: "General session instructions", value: goal.general_session_instructions }
      : null,
    goal.reinforcement_context
      ? { label: "Reinforcement context", value: goal.reinforcement_context }
      : null,
    goal.strategy_notes ? { label: "Strategy notes", value: goal.strategy_notes } : null,
  ].filter((n): n is CriteriaNote => n !== null);
}

/**
 * Returns true when every active (non-archived) target has status "met",
 * used to decide whether to show the MasteryBanner at "allTargetsMet" level.
 */
export function allTargetsMet(targets: Pick<GoalTarget, "status">[]): boolean {
  return targets.length > 0 && targets.every((t) => t.status === "met");
}
